import React, { FC } from 'react'

import styled from 'styled-components'
import { Skeleton } from 'antd'
import { SkeletonNodeProps } from 'antd/es/skeleton/Node'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'
import { TrackInfo } from 'src/Components/TrackInfo'
import { CampaignPayment } from 'src/Redux/artistOrderApi/campaignPayment'
import { RelaunchButton } from 'src/Components/Buttons/RelaunchButton'
import { FIXED_BY } from 'src/Constants/constants'
import { UpsellButton } from 'src/Components/Buttons/UpsellButton'

import { BudgetSection } from '../BudgetSection'

const stoppedOrEndedStatuses = [
  CampaignStatusEnum.STOPPED,
  CampaignStatusEnum.ENDED,
  CampaignStatusEnum.STOPPED_BY_ADMIN,
]

const getSpendString = (
  spent: string | undefined,
  budget: string | undefined,
  refund: string | undefined,
  campaignStatus: CampaignStatusEnum | undefined,
): string | undefined => {
  if (!campaignStatus || !stoppedOrEndedStatuses.includes(campaignStatus)) {
    return spent
  }
  const budgetNumber = isNaN(Number(budget)) ? 0 : Number(budget)
  const refundNumber = isNaN(Number(refund)) ? 0 : Number(refund)
  return refundNumber ? (budgetNumber - refundNumber).toFixed(FIXED_BY) : spent
}

const StyledUpsellButton = styled(UpsellButton)`
  div > span {
    display: block;
  }
`

const SummaryWrapper = styled.div`
  display: flex;
  width: 320px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  position: sticky;
  top: 0px;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    position: relative;
    width: 100%;
    gap: 48px;
  }
`

const StyledSkeleton = styled(({ className, ...rest }: SkeletonNodeProps) => (
  <Skeleton.Input {...rest} rootClassName={className} active />
))`
  &.ant-skeleton-block {
    border-radius: 16px;
  }
  &.ant-skeleton {
    flex-grow: 1 !important;
    @media (max-width: ${MAX_MOBILE_WIDTH}) {
      width: 100% !important;
    }
  }
  & > .ant-skeleton-input {
    width: 100% !important;
    height: 240px !important;
    border-radius: 16px !important;
    background: ${({ theme }) => theme.color.lightGrey} !important;
  }
`

interface SummarySectionProps {
  loading: boolean
  campaignStatus?: CampaignStatusEnum
  payment?: CampaignPayment | null
  budget?: string
  spent?: string
  refund?: string
  trackCoverUrl?: string
  trackName?: string
  trackAuthorName?: string
  startDate?: string | null
  endDate?: string | null
  genres?: Array<string>
  onUpsellCampaign: () => void
  onRelaunchCampaign: () => void
  isRelaunchAvailable?: boolean
  matchingType?: SpotifyCampaignMatchingType | null
  isPreRelease?: boolean
}

export const SummarySection: FC<SummarySectionProps> = ({
  campaignStatus,
  payment,
  budget,
  loading,
  trackCoverUrl,
  trackName,
  refund,
  trackAuthorName,
  startDate,
  endDate,
  genres,
  spent,
  onUpsellCampaign,
  onRelaunchCampaign,

  isRelaunchAvailable,
  matchingType,
  isPreRelease,
}) => {
  const isCampaignStoppedOrEnded =
    campaignStatus &&
    [
      CampaignStatusEnum.STOPPED,
      CampaignStatusEnum.ENDED,
      CampaignStatusEnum.STOPPED_BY_ADMIN,
    ].includes(campaignStatus)

  if (loading) {
    return <StyledSkeleton />
  }
  if (!campaignStatus) {
    return null
  }

  const spendString = getSpendString(spent, budget, refund, campaignStatus)

  return (
    <SummaryWrapper>
      <TrackInfo
        platform={CampaignPlatformTypeEnum.SPOTIFY}
        status={campaignStatus}
        paymentState={payment?.state}
        trackImage={trackCoverUrl}
        trackName={trackName}
        artistName={trackAuthorName}
        startDate={startDate}
        endDate={endDate}
        genres={genres}
        matchingType={matchingType}
        isPreRelease={isPreRelease}
      >
        {isRelaunchAvailable && <RelaunchButton onClick={onRelaunchCampaign} />}

        {isCampaignStoppedOrEnded && (
          <StyledUpsellButton
            onClick={onUpsellCampaign}
            platform={CampaignPlatformTypeEnum.TIKTOK}
          />
        )}
      </TrackInfo>
      <BudgetSection
        campaignStatus={campaignStatus}
        budget={budget}
        spent={spendString}
      />
    </SummaryWrapper>
  )
}
